import { Component } from 'preact';
import { motion } from 'framer-motion';
import {
  TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  GOLD, GOLD_BTN, BEVELED_BTN, PANEL_BG, FourCorners, VIGNETTE,
  ACCENT_GOLD, TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE, OrnamentalDivider,
} from '../../lib/medievalTheme';
import { getGameMode, loadModeSetup, saveModeSetup } from '../../utils/arena/gameModes';
import { isFreePlay } from '../../utils/freePlay';
import { BOOSTER_SETS } from '../../utils/arena/packsApi';
import TutorialOverlay from '../TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../../utils/arena/tutorialState';
import { isPhoneLayout } from '../../utils/platform';
import { k, t } from '../../localization/t';

// 1cqw = 1rem at design scale, so the same numbers render the identical
// look: canvas-relative on desktop, device-absolute (tappable) on phone.
const U = isPhoneLayout() ? 'rem' : 'cqw';

const MATCH_SETUP_TUTORIAL_KEY = 'match-setup-v1';

const MATCH_SETUP_TUTORIAL_STEPS = [
  {
    key: 'format',
    title: k('Format'),
    body: k('Constructed is regular Sorcery: every rarity is fair game. Peasant is the same game, restricted to Ordinary and Exceptional cards.'),
    selector: '[data-tutorial="setup-format"]',
  },
  {
    key: 'stakes',
    title: k('Stakes'),
    body: k('Ranked puts your Legend Points on the line: wins climb the leaderboard, losses cost you points. Casual keeps the same coins, XP and shards but leaves your rank untouched. Both pay out. Only Sandbox plays for nothing.'),
    freePlayBody: k('Ranked puts your Legend Points on the line: wins climb the leaderboard, losses cost you points. Casual leaves your rank untouched.'),
    selector: '[data-tutorial="setup-stakes"]',
  },
];

// Format options for Match mode. Visual: each card carries a glyph
// summary on the right (rarity dots for the legality story) so the
// pick reads as a choice between two distinct rulesets, not two
// strings.
const MATCH_FORMAT_OPTIONS = [
  {
    value: 'constructed',
    label: k('Constructed'),
    tagline: k('Standard ranked queue'),
    description: k('All rarities are legal. The full card pool, no restrictions.'),
    rarityDots: ['Ordinary', 'Exceptional', 'Elite', 'Unique'],
  },
  {
    value: 'peasant',
    label: k('Peasant'),
    tagline: k('Lower-rarity ranked'),
    description: k('Only Ordinary and Exceptional cards.'),
    rarityDots: ['Ordinary', 'Exceptional'],
  },
];

// Hit-point options. "Quick" gets a distinctive bolt glyph; "Standard"
// uses a shield. The HP numeral on the right accessory already says
// what each one does. No body description needed; they read at a
// glance.
const HP_OPTIONS = [
  { value: 10, label: k('Quick Match') },
  { value: 20, label: k('Standard') },
];

// Automation options. Shown for EVERY mode. A draft round and a solo sandbox
// are as much a play session as a ranked match, and the choice belongs to the
// session, not to a global preference. Manual is the default: the automation
// engine only covers part of the card pool, so a table that resolves nothing
// is the predictable one.
const AUTOMATION_OPTIONS = [
  {
    value: false,
    label: k('Manual'),
    tagline: k('Tabletop rules'),
    description: k('You handle the cards and rules yourself.'),
  },
  {
    value: true,
    label: k('Automated'),
    tagline: k('Experimental'),
    description: k('CAUTION: Support is still being worked on. Expect bugs.'),
  },
];

// Stakes options for Match mode. Both pay the SAME coins, XP and
// shards. The only difference is whether the result moves the ladder.
// This used to be a single on/off "Compete for Legend Points" switch,
// which read as "on = a real match, off = nothing counts": players left
// it off, played a full game and got no rewards (issue #41). Two tiles
// that each spell out their payout remove the guess.
const STAKES_OPTIONS = [
  {
    value: true,
    label: k('Ranked'),
    tagline: k('Leaderboard'),
    description: k('Full rewards. Wins and losses move your Legend Points and rank.'),
    freePlayDescription: k('Wins and losses move your Legend Points and rank.'),
  },
  {
    value: false,
    label: k('Casual'),
    tagline: k('Off the ladder'),
    description: k('Full rewards. Coins, XP and shards. Your rank never moves.'),
    freePlayDescription: k('Just for the game. Your rank never moves.'),
  },
];

const RARITY_DOT_COLOR = {
  Ordinary: '#9aa1a8',
  Exceptional: '#5fa9c9',
  Elite: '#b78ad6',
  Unique: '#d4a843',
};

function RarityDots({ rarities }) {
  return (
    <div className="flex items-center gap-1">
      {rarities.map((r) => (
        <span
          key={r}
          title={t(r)}
          style={{
            width: `0.4${U}`,
            height: `0.4${U}`,
            borderRadius: '50%',
            background: RARITY_DOT_COLOR[r] || '#fff',
            boxShadow: `0 0 0.25${U} ${RARITY_DOT_COLOR[r] || '#fff'}33`,
            display: 'inline-block',
          }}
        />
      ))}
    </div>
  );
}

// Bolt glyph for Quick Match. Visually conveys "fast / dangerous".
function QuickGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: '#f0b260' }}>
      <path d="m13 2-2 7h6l-9 13 2-9H4z" />
    </svg>
  );
}

// Shield glyph for Standard. Visually conveys "durable / classic".
function StandardGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: ACCENT_GOLD }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </svg>
  );
}

// Hand glyph for Manual. You move the pieces.
function ManualGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: ACCENT_GOLD }}>
      <path d="M11 10V4.5a1.5 1.5 0 0 1 3 0V10" />
      <path d="M14 10V3.5a1.5 1.5 0 0 1 3 0V10" />
      <path d="M17 10V5.5a1.5 1.5 0 0 1 3 0v8a8 8 0 0 1-8 8h-2a8 8 0 0 1-8-8v-1a1.5 1.5 0 0 1 3 0" />
      <path d="M8 12.5V6a1.5 1.5 0 0 1 3 0v4" />
    </svg>
  );
}

// Trophy glyph for Ranked. The ladder is at stake.
function RankedGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: ACCENT_GOLD }}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.7V17c0 .6-.5 1-1 1.2C7.9 18.8 7 20.2 7 22" />
      <path d="M14 14.7V17c0 .6.5 1 1 1.2 1.1.6 2 2 2 3.8" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

// Crossed-swords glyph for Casual. A real fight, no ladder.
function CasualGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: '#9aa1a8' }}>
      <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
      <path d="m13 19 6-6" />
      <path d="m16 16 4 4" />
      <path d="m19 21 2-2" />
      <path d="M14.5 6.5 18 3h3v3l-3.5 3.5" />
      <path d="m5 14 4 4" />
      <path d="m7 17-3 3" />
      <path d="m3 19 2 2" />
    </svg>
  );
}

// Gear glyph for Automated. The engine turns for you.
function AutomatedGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: `1.25${U}`, height: `1.25${U}`, color: '#f0b260' }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.1a2 2 0 1 1-4 0v-.2a1.7 1.7 0 0 0-3-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.1-3l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 2.9-1.2V3a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 3 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

export const SECTION_LABEL_STYLE = {
  color: `${GOLD} 0.55)`,
 fontSize: `0.75${U}`,
 fontWeight: 700,
 fontFamily: "'FantaisieArtistique', cursive",
};

export const HEADER_TITLE_STYLE = {
 fontSize: `1.625${U}`,
 lineHeight: 1.05,
 letterSpacing: '0.04em',
 fontWeight: 800,
 background: 'linear-gradient(180deg, #f7e4b6 0%, #d4a843 60%, #a8772d 100%)',
 WebkitBackgroundClip: 'text',
 WebkitTextFillColor: 'transparent',
 backgroundClip: 'text',
 textShadow: '0 1px 0 rgba(0,0,0,0.4), 0 0 18px rgba(212,168,67,0.18)',
 fontFamily: "'FantaisieArtistique', cursive",
};

// Tile shell shared by Format and Match-Length pickers. The `cursor`
// property is intentionally omitted. Leaving it off lets the global
// `button { cursor: url(pointer.png) ... }` rule in index.css supply
// our medieval cursor. Setting `cursor: 'pointer'` inline here would
// outscore the global rule via the cascade and the player would see
// the OS pointer instead of the medieval one.
const TILE_BASE = {
  borderRadius: `0.5${U}`,
  padding: `0.85${U} 0.95${U}`,
  transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
  textAlign: 'left',
  position: 'relative',
  overflow: 'hidden',
};

export const TILE_INACTIVE = {
  ...TILE_BASE,
  ...SUB_PANEL_STYLE,
};

export const TILE_ACTIVE = {
  ...TILE_BASE,
  ...SUB_PANEL_STYLE,
  border: `1px solid ${ACCENT_GOLD}`,
  boxShadow: [
    `inset 0 2px 4px rgba(0,0,0,0.55)`,
    `inset 0 -1px 0 rgba(255,235,195,0.06)`,
    `inset 0 0 0 1px ${GOLD} 0.18)`,
    `inset 0 0 28px rgba(0,0,0,0.45)`,
    `0 0 1.25${U} ${GOLD} 0.28)`,
    `0 0 0.25${U} ${ACCENT_GOLD}`,
  ].join(', '),
  transform: 'translateY(-1px)',
};

/**
 * Mode-specific configuration dialog shown after picking a mode
 * on ModePickerScreen. Restyled to read as a triple-A game UI:
 * textured stone panel, ornamental dividers, gradient-gold title,
 * section-by-section selection tiles.
 *
 * Automation (manual vs. automated) is asked for EVERY mode. The rest
 * varies:
 *
 *   Match   → format (Constructed/Peasant) + starting HP (10/20)
 *             + stakes (Ranked / Casual). Deck picker fires after
 *             confirm.
 *   Sandbox → ranked opt-in only (rest decided in-game). Deck picker
 *             fires after confirm.
 *   Draft   → format (booster set) + pod size. No deck picker.
 *   Sealed  → format + pod size.
 */
export default class ModeSetupDialog extends Component {
  constructor(props) {
    super(props);
    const mode = getGameMode(props.modeKey);
    // Last confirmed setup for this mode. Every field is validated
    // against the options this dialog actually renders. A stored
    // value can predate an option being removed or a mode's seat
    // range changing.
    const saved = loadModeSetup(props.modeKey);
    const seatFloor = mode?.seatsMin || 2;
    const seatCeil = mode?.seatsMax || 2;
    const clampSeats = (v, fallback) => (
      Number.isFinite(v) ? Math.min(Math.max(v, seatFloor), seatCeil) : fallback
    );
    this.state = {
      // Default to Ranked so the player has to pick OUT of the ladder.
      // The reasoning: Legend Points are the season's headline
      // progression hook, and most players entering 1v1 expect their
      // results to count. Landing on Casual by accident is the milder
      // failure of the two. It still pays coins, XP and shards.
      rankedOptIn: typeof saved.rankedOptIn === 'boolean' ? saved.rankedOptIn : true,
      seatsMin: clampSeats(saved.seatsMin, seatFloor),
      seatsMax: clampSeats(saved.seatsMax, seatCeil),
      // For 'match' mode this carries the deck format ('constructed'
      // or 'peasant'); for 'draft' / 'sealed' it carries the booster
      // set key. Default 'constructed' so a Match user who never
      // touches the format picker still ends up in the standard queue.
      format: props.modeKey === 'match'
        ? (MATCH_FORMAT_OPTIONS.some((o) => o.value === saved.format) ? saved.format : 'constructed')
        : null,
      // Starting HP for 'match' mode. 20 is the rulebook default;
      // 10 is "Quick Match." Same rules, half the life pool, ~half
      // the match length. Other modes ignore this field.
      startingHp: HP_OPTIONS.some((o) => o.value === saved.startingHp) ? saved.startingHp : 20,
      // Session-wide automation mode, shared by every seat. Off by default:
      // the automation engine covers only part of the card pool, so the
      // predictable table is the one that resolves nothing.
      automations: saved.automations === true,
      // Default to 'beta'. The only booster set currently unlocked
      // for every account. User can switch via the picker below
      // for draft / sealed.
      setKey: BOOSTER_SETS[saved.setKey] ? saved.setKey : 'beta',
      showSetupTutorial: false,
    };
  }

  componentDidMount() {
    if (this.props.modeKey !== 'match') return;
    const profileId = this.props.profile?.id;
    if (!profileId) return;
    hydrateTutorialState().then(() => {
      if (this._unmounted) return;
      if (!shouldAutoPlay(profileId, MATCH_SETUP_TUTORIAL_KEY)) return;
      this._tutorialTimer = setTimeout(() => {
        if (!this._unmounted) this.setState({ showSetupTutorial: true });
      }, 350);
    });
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
  }

  handleSetupTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, MATCH_SETUP_TUTORIAL_KEY);
    this.setState({ showSetupTutorial: false });
  };

  handleConfirm = () => {
    const { rankedOptIn, seatsMin, seatsMax, format, setKey, startingHp, automations } = this.state;
    const cfg = { rankedOptIn, seatsMin, seatsMax, format, setKey, startingHp, automations };
    saveModeSetup(this.props.modeKey, cfg);
    this.props.onConfirm?.(cfg);
  };

  // ── Phone layout ────────────────────────────────────────────────
  // Dedicated full-screen composition, not the desktop dialog scaled
  // down: a one-line top bar, one row per section (label left, compact
  // segmented pills right, one short sub-line instead of prose), and a
  // pinned footer. A Match setup fits a 390px-tall landscape phone with
  // no scrolling.

  renderPhoneSeg({ active, onClick, key, title, sub, subColor }) {
    return (
      <button
        key={key}
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5"
        style={{
          ...(active ? TILE_ACTIVE : TILE_INACTIVE),
          transform: 'none',
          padding: '0.375rem 0.5rem',
          minHeight: '2.9rem',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <span
          className="font-bold leading-none"
          style={{
            fontSize: '0.875rem',
            color: active ? TEXT_PRIMARY : TEXT_BODY,
            fontFamily: "'FantaisieArtistique', cursive",
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </span>
        {sub ? (
          <span
            className="leading-none whitespace-nowrap"
            style={{ fontSize: '0.625rem', color: subColor || (active ? `${GOLD} 0.85)` : TEXT_MUTED) }}
          >
            {sub}
          </span>
        ) : null}
      </button>
    );
  }

  renderPhoneRow({ label, tutorial, children }) {
    return (
      <div className="flex items-center gap-3" data-tutorial={tutorial}>
        <span
          className="shrink-0 text-right"
          style={{ ...SECTION_LABEL_STYLE, fontSize: '0.625rem', width: '5.5rem' }}
        >
          {label}
        </span>
        <div className="flex-1 min-w-0 flex gap-2">{children}</div>
      </div>
    );
  }

  renderPhone({ mode, needsDeck, supportsRanked, supportsFormat, supportsHp }) {
    const { onCancel, visibility } = this.props;
    // Solid backgroundColor: the panel color is rgba(...,0.92) and this
    // full-screen view has no dim overlay behind it, so the play screen
    // would bleed through the texture.
    return (
      <div
        className="fixed inset-0 z-[80] flex flex-col"
        style={{
          ...TEXTURED_PANEL_STYLE,
          borderRadius: 0,
          backgroundColor: '#0c0a08',
          // A full-screen view has no frame: the panel's gold border +
          // ring shadows read as a rectangular stroke fighting the
          // phone's rounded screen corners. Keep only the soft vignette.
          border: 'none',
          boxShadow: 'inset 0 0 60px rgba(0,0,0,0.55)',
        }}
      >
        {/* One-line top bar in place of the desktop's stacked header. */}
        {/* Fixed inset-0 spans the canvas' padding box, so the canvas'
            safe-area padding does not apply here: every edge needs its
            own env() inset (notch left/right in landscape, home
            indicator bottom). */}
        <div
          className="shrink-0 flex items-baseline gap-2 pb-1.5"
          style={{
            paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
            paddingTop: 'max(0.625rem, env(safe-area-inset-top, 0px))',
          }}
        >
          <h2 style={{ ...HEADER_TITLE_STYLE, fontSize: '1.125rem' }}>
            {t('Set Up {mode}', { mode: mode?.title ? t(mode.title) : t('Match') })}
          </h2>
          <span style={{ fontSize: '0.6875rem', color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}>
            {visibility}
          </span>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto"
          style={{
            paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
          }}
        >
          {/* Capped column: flex-1 pills against the raw viewport width
              inflate to ~2x their content on wide phones and desktop
              windows. ~30rem keeps each pill about two words wide and
              centers the block like a settings card. */}
          <div className="flex flex-col gap-2 py-1.5 my-auto min-h-full justify-center w-full mx-auto" style={{ maxWidth: '30rem' }}>
            {supportsFormat ? (
              this.renderPhoneRow({
                label: t('Format'),
                tutorial: 'setup-format',
                children: MATCH_FORMAT_OPTIONS.map((opt) => this.renderPhoneSeg({
                  active: this.state.format === opt.value,
                  onClick: () => this.setState({ format: opt.value }),
                  key: opt.value,
                  title: t(opt.label),
                  sub: opt.value === 'constructed' ? t('All rarities') : t('Ordinary + Exceptional'),
                })),
              })
            ) : null}

            {supportsHp ? (
              this.renderPhoneRow({
                label: t('Match Length'),
                children: HP_OPTIONS.map((opt) => this.renderPhoneSeg({
                  active: this.state.startingHp === opt.value,
                  onClick: () => this.setState({ startingHp: opt.value }),
                  key: opt.value,
                  title: t(opt.label),
                  sub: t('{n} HP', { n: opt.value }),
                })),
              })
            ) : null}

            {supportsRanked ? (
              this.renderPhoneRow({
                label: t('Stakes'),
                tutorial: 'setup-stakes',
                children: STAKES_OPTIONS.map((opt) => this.renderPhoneSeg({
                  active: this.state.rankedOptIn === opt.value,
                  onClick: () => this.setState({ rankedOptIn: opt.value }),
                  key: String(opt.value),
                  title: t(opt.label),
                  sub: opt.value ? t('Moves your rank') : t('Off the ladder'),
                })),
              })
            ) : null}

            {this.renderPhoneRow({
              label: t('Automation'),
              children: AUTOMATION_OPTIONS.map((opt) => this.renderPhoneSeg({
                active: this.state.automations === opt.value,
                onClick: () => this.setState({ automations: opt.value }),
                key: String(opt.value),
                title: t(opt.label),
                sub: opt.value ? t('Experimental') : t('Tabletop rules'),
                subColor: opt.value ? '#e6b34a' : undefined,
              })),
            })}
          </div>
        </div>

        <div
          className="shrink-0 flex justify-end gap-2 pt-2"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.42) 100%)',
            borderTop: `1px solid ${GOLD} 0.08)`,
            paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
            paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <button
            type="button"
            className="px-5 text-sm transition-all duration-150 active:scale-[0.98]"
            style={{ ...BEVELED_BTN, minHeight: '2.5rem', borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.04em', color: TEXT_BODY }}
            onClick={onCancel}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className="px-6 text-sm font-semibold transition-all duration-150 active:scale-[0.98]"
            style={{ ...GOLD_BTN, minHeight: '2.5rem', borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.06em' }}
            onClick={this.handleConfirm}
          >
            {needsDeck ? t('Pick Deck →') : t('Create')}
          </button>
        </div>
        {this.state.showSetupTutorial && (
          <TutorialOverlay
            steps={MATCH_SETUP_TUTORIAL_STEPS.map((s) => ({ ...s, title: t(s.title), body: t((isFreePlay() && s.freePlayBody) || s.body) }))}
            onDismiss={this.handleSetupTutorialDismiss}
          />
        )}
      </div>
    );
  }

  renderTile({ active, onClick, key, leadGlyph, label, tagline, description, rightAccessory }) {
    return (
      <button
        key={key}
        type="button"
        onClick={onClick}
        style={active ? TILE_ACTIVE : TILE_INACTIVE}
        className="flex flex-col gap-1.5 w-full"
      >
        <div className="flex items-center gap-2">
          {leadGlyph}
          <div className="flex flex-col min-w-0 flex-1">
            <div
              className="text-sm font-bold"
              style={{
                color: active ? TEXT_PRIMARY : TEXT_BODY,
                fontFamily: "'FantaisieArtistique', cursive",
                letterSpacing: '0.02em',
              }}
            >
              {label}
            </div>
            {tagline ? (
              <div
                className="text-[0.625cqw]"
                style={{ color: active ? `${GOLD} 0.85)` : `${GOLD} 0.5)` }}
              >
                {tagline}
              </div>
            ) : null}
          </div>
          {rightAccessory ? <div className="shrink-0">{rightAccessory}</div> : null}
        </div>
        {description ? (
          <div
            className="text-[0.6875cqw] leading-snug"
            style={{ color: active ? TEXT_BODY : TEXT_MUTED }}
          >
            {description}
          </div>
        ) : null}
      </button>
    );
  }

  renderSectionLabel(text) {
    return (
      <div className="flex items-center gap-2 mb-1.5">
        <div className="h-px w-3" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.55) 100%)` }} />
        <span style={SECTION_LABEL_STYLE}>{text}</span>
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${GOLD} 0.55) 0%, transparent 100%)` }} />
      </div>
    );
  }

  render() {
    const { onCancel, modeKey, visibility } = this.props;
    const mode = getGameMode(modeKey);
    const needsDeck = modeKey === 'match' || modeKey === 'sandbox';
    const supportsRanked = mode?.supportsRanked && visibility !== 'solo';
    const isFlexSeat = modeKey === 'draft' || modeKey === 'sealed';
    const supportsFormat = modeKey === 'match';
    const supportsHp = modeKey === 'match';

    if (isPhoneLayout()) {
      return this.renderPhone({ mode, needsDeck, supportsRanked, supportsFormat, supportsHp });
    }

    return (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(2px)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.99 }}
          transition={{ duration: 0.28, ease: [0.22, 0.8, 0.36, 1] }}
          className="relative w-[40cqw] max-h-[90cqh] flex flex-col overflow-hidden"
          style={TEXTURED_PANEL_STYLE}
        >
          {/* Just the L-shaped corner platings. No knot ornaments.
              Radius matches TEXTURED_PANEL_STYLE's borderRadius
              (0.5cqw = 8 design-px); a larger value here makes the
              L-shaped corner platings curve outside the panel's
              clipped edge and the corners read as "broken." */}
          <FourCorners />

          {/* Header. Gradient gold title + small visibility/mode line.
              No bottom border; we use OrnamentalDivider below instead so
              the seam reads like part of the medieval frame, not a
              flat HTML hairline. */}
          <div className="px-7 pt-5 pb-3 text-center relative">
            <h2 style={HEADER_TITLE_STYLE}>
              {t('Set Up {mode}', { mode: mode?.title ? t(mode.title) : t('Match') })}
            </h2>
            <div
              className="text-[0.6875cqw] mt-1"
              style={{ color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}
            >
              {visibility} &middot; {mode?.tagline ? t(mode.tagline) : mode?.tagline}
            </div>
          </div>
          <div className="px-7">
            <OrnamentalDivider />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto medieval-scrollbar px-7 py-5 flex flex-col gap-5">
            <div>
              {this.renderSectionLabel(t('Automation'))}
              <div className="grid grid-cols-2 gap-3">
                {AUTOMATION_OPTIONS.map((opt) => this.renderTile({
                  active: this.state.automations === opt.value,
                  onClick: () => this.setState({ automations: opt.value }),
                  key: String(opt.value),
                  label: t(opt.label),
                  tagline: t(opt.tagline),
                  description: t(opt.description),
                  leadGlyph: opt.value ? <AutomatedGlyph /> : <ManualGlyph />,
                }))}
              </div>
            </div>

            {supportsFormat ? (
              <div data-tutorial="setup-format">
                {this.renderSectionLabel(t('Format'))}
                <div className="grid grid-cols-2 gap-3">
                  {MATCH_FORMAT_OPTIONS.map((opt) => this.renderTile({
                    active: this.state.format === opt.value,
                    onClick: () => this.setState({ format: opt.value }),
                    key: opt.value,
                    label: t(opt.label),
                    tagline: t(opt.tagline),
                    description: t(opt.description),
                    rightAccessory: <RarityDots rarities={opt.rarityDots} />,
                  }))}
                </div>
              </div>
            ) : null}

            {supportsHp ? (
              <div>
                {this.renderSectionLabel(t('Match Length'))}
                <div className="grid grid-cols-2 gap-3">
                  {HP_OPTIONS.map((opt) => this.renderTile({
                    active: this.state.startingHp === opt.value,
                    onClick: () => this.setState({ startingHp: opt.value }),
                    key: opt.value,
                    label: t(opt.label),
                    tagline: opt.tagline,
                    description: opt.description,
                    leadGlyph: opt.value === 10 ? <QuickGlyph /> : <StandardGlyph />,
                    rightAccessory: (
                      <div
                        className="flex items-baseline gap-0.5 tabular-nums"
                        style={{ color: this.state.startingHp === opt.value ? ACCENT_GOLD : `${GOLD} 0.5)` }}
                      >
                        <span style={{ fontSize: `1.25${U}`, fontWeight: 800, fontFamily: "'FantaisieArtistique', cursive" }}>{opt.value}</span>
                        <span className="text-[0.5625cqw]">{t('HP')}</span>
                      </div>
                    ),
                  }))}
                </div>
              </div>
            ) : null}

            {supportsRanked ? (
              <div data-tutorial="setup-stakes">
                {this.renderSectionLabel(t('Stakes'))}
                <div className="grid grid-cols-2 gap-3">
                  {STAKES_OPTIONS.map((opt) => this.renderTile({
                    active: this.state.rankedOptIn === opt.value,
                    onClick: () => this.setState({ rankedOptIn: opt.value }),
                    key: String(opt.value),
                    label: t(opt.label),
                    tagline: t(opt.tagline),
                    description: t((isFreePlay() && opt.freePlayDescription) || opt.description),
                    leadGlyph: opt.value ? <RankedGlyph /> : <CasualGlyph />,
                  }))}
                </div>
                {/* Named so the contrast with Sandbox. The one mode
                    that really does pay nothing. Is on screen at the
                    moment of choosing, not something to find out after
                    a 24-minute game. */}
                {isFreePlay() ? null : (
                  <div className="text-[0.625cqw] mt-2" style={{ color: TEXT_MUTED }}>
                    {t('Neither is Sandbox: only Sandbox matches award nothing at all.')}
                  </div>
                )}
              </div>
            ) : null}

            {isFlexSeat ? (
              <div className="flex flex-col gap-4">
                <div>
                  {this.renderSectionLabel(t('Booster Set'))}
                  <select
                    className="w-full text-sm px-3 py-2 cursor-pointer"
                    style={{
                      background: 'rgba(0,0,0,0.45)',
                      border: `1px solid ${GOLD} 0.35)`,
                      borderRadius: `0.375${U}`,
                      color: TEXT_PRIMARY,
                      fontFamily: "'FantaisieArtistique', cursive",
                    }}
                    value={this.state.setKey}
                    onChange={(e) => this.setState({ setKey: e.currentTarget.value })}
                  >
                    {Object.values(BOOSTER_SETS).map((s) => (
                      <option key={s.name} value={s.name}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  {this.renderSectionLabel(t('Pod Size'))}
                  <div
                    className="flex flex-col gap-3 px-3 py-3"
                    style={SUB_PANEL_STYLE}
                  >
                    <label className="text-sm flex flex-col gap-1" style={{ color: TEXT_BODY }}>
                      <span className="flex items-center justify-between">
                        <span className="text-[0.6875cqw]" style={{ color: `${GOLD} 0.55)` }}>{t('Minimum to start')}</span>
                        <span className="tabular-nums font-bold" style={{ color: ACCENT_GOLD }}>{this.state.seatsMin}</span>
                      </span>
                      <input
                        type="range"
                        min={mode?.seatsMin || 2}
                        max={mode?.seatsMax || 8}
                        step={1}
                        value={this.state.seatsMin}
                        onInput={(e) => {
                          const v = Number(e.currentTarget.value);
                          this.setState((s) => ({
                            seatsMin: v,
                            seatsMax: Math.max(v, s.seatsMax),
                          }));
                        }}
                        className="w-full"
                      />
                    </label>
                    <label className="text-sm flex flex-col gap-1" style={{ color: TEXT_BODY }}>
                      <span className="flex items-center justify-between">
                        <span className="text-[0.6875cqw]" style={{ color: `${GOLD} 0.55)` }}>{t('Maximum players')}</span>
                        <span className="tabular-nums font-bold" style={{ color: ACCENT_GOLD }}>{this.state.seatsMax}</span>
                      </span>
                      <input
                        type="range"
                        min={this.state.seatsMin}
                        max={mode?.seatsMax || 8}
                        step={1}
                        value={this.state.seatsMax}
                        onInput={(e) => this.setState({ seatsMax: Number(e.currentTarget.value) })}
                        className="w-full"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer. Same ornamental seam at the bottom, then the
              actions on a thin recessed strip so the buttons "sit on"
              the panel rather than floating. */}
          <div className="px-7">
            <OrnamentalDivider />
          </div>
          <div
            className="flex justify-end gap-3 px-7 py-4"
            style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.42) 100%)',
              borderTop: `1px solid ${GOLD} 0.08)`,
            }}
          >
            <button
              type="button"
              className="px-5 py-2 text-sm cursor-pointer transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                ...BEVELED_BTN,
                borderRadius: `0.375${U}`,
                fontFamily: "'FantaisieArtistique', cursive",
                letterSpacing: '0.04em',
                color: TEXT_BODY,
              }}
              onClick={onCancel}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="px-6 py-2 text-sm font-semibold cursor-pointer transition-all duration-150 hover:scale-[1.03] active:scale-[0.98]"
              style={{
                ...GOLD_BTN,
                borderRadius: `0.375${U}`,
                fontFamily: "'FantaisieArtistique', cursive",
                letterSpacing: '0.06em',
              }}
              onClick={this.handleConfirm}
            >
              {needsDeck ? t('Pick Deck →') : t('Create')}
            </button>
          </div>
        </motion.div>
        {this.state.showSetupTutorial && (
          <TutorialOverlay
            steps={MATCH_SETUP_TUTORIAL_STEPS.map((s) => ({ ...s, title: t(s.title), body: t((isFreePlay() && s.freePlayBody) || s.body) }))}
            onDismiss={this.handleSetupTutorialDismiss}
          />
        )}
      </div>
    );
  }
}
